type ValidationError = {
  errors: Record<string, string[]>
  message: string
}
function isValidationError(obj: unknown): obj is ValidationError {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "message" in obj &&
    typeof obj.message === "string" &&
    "errors" in obj &&
    obj.errors !== null &&
    typeof obj.errors === "object" &&
    Object.values(obj.errors).every(
      (value) =>
        Array.isArray(value) && value.every((item) => typeof item === "string"),
    )
  )
}

/** Returns the error key we get from the api. For example `api.messages.invalid_data` */
export function getErrorKey(error: unknown): string | undefined {
  if (
    typeof error == "object" &&
    error !== null &&
    "data" in error &&
    typeof error.data === "object" &&
    error.data !== null &&
    "message" in error.data &&
    typeof error.data.message === "string"
  ) {
    return error.data.message
  }
}

export function useLocalisedError() {
  const { t, te } = useI18n()

  const getErrorText = (_error: MaybeRef): string | string[] | undefined => {
    const error = unref(_error)
    if (!error) {
      return undefined
    }
    if (!getErrorKey(error)) {
      return t("dynamic_keys.api.errors.unknown")
    }

    const errorData = error.data
    if (
      isValidationError(errorData) &&
      // some endpoints return an empty `errors` array. In that case we should look at `message` instead.
      Object.values(errorData.errors).length > 0
    ) {
      return Object.entries(errorData.errors).flatMap(([key, messages]) =>
        messages.map((message) => {
          // The message is either a translation key, in which case we can look it up
          // In other cases it's an English error message like "The last name field is required"
          return te(`dynamic_keys.api.validations.messages.${message}`)
            ? t(`dynamic_keys.api.validations.messages.${message}`, {
                _field_: key,
              })
            : message
        }),
      )
    }

    if (error.data.status == 419) {
      return t("dynamic_keys.api.errors.csrf")
    }
    const errorKey = getErrorKey(error)
    return errorKey && te(`dynamic_keys.${errorKey}`)
      ? t(`dynamic_keys.${errorKey}`)
      : t("dynamic_keys.api.errors.unknown")
  }
  return { getErrorText }
}
